<template>
  <b-sidebar
    id="edit-price-list-sidebar"
    :visible="priceListEditSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(visible) => $emit('update:price-list-edit-sidebar-active', visible)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Editar Lista de Precios
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer ref="simpleRules">
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="onReset"
        >
          <!-- Información General -->
          <h4 class="mb-2">
            <feather-icon
              icon="TagIcon"
              size="18"
              class="mr-1"
            />
            Información General
          </h4>

          <b-row>
            <b-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                name="nombre"
                :rules="required"
              >
                <b-form-group
                  label="Nombre:"
                  label-for="input-name"
                >
                  <b-form-input
                    id="input-name"
                    v-model="form.name"
                    placeholder="Ingresa el nombre de la lista de precios"
                    :state="!errors[0]"
                    required
                  />
                  <b-form-invalid-feedback>
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="descripción"
                rules="required"
              >
                <b-form-group
                  label="Descripción:"
                  label-for="input-description"
                >
                  <b-form-textarea
                    id="input-description"
                    v-model="form.description"
                    placeholder="Ingresa la descripción de la lista de precios"
                    :state="!errors[0]"
                    rows="3"
                    required
                  />
                  <b-form-invalid-feedback>
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Estado -->
          <h4 class="mb-2 mt-1">
            <feather-icon
              icon="ToggleRightIcon"
              size="18"
              class="mr-1"
            />
            Estado
          </h4>

          <b-row>
            <b-col cols="12">
              <b-form-group>
                <b-form-checkbox
                  v-model="form.active"
                  name="active"
                  value="1"
                  unchecked-value="0"
                >
                  Lista de precios activa
                </b-form-checkbox>
                <b-form-text>
                  Las listas inactivas no se mostrarán en las opciones de clientes
                </b-form-text>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Botones de acción -->
          <div class="d-flex mt-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              variant="primary"
              class="mr-1"
              :disabled="isSubmitting"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              />
              Actualizar
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="reset"
              variant="outline-secondary"
              @click="hide"
            >
              Cancelar
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, decimal } from '@/utils/validations/validations'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },

  directives: {
    Ripple,
  },

  props: {
    priceListId: {
      type: [Number, String],
      required: true,
    },
    priceListEditSidebarActive: {
      type: Boolean,
      default: false,
      required: true,
    },
  },

  data() {
    return {
      required,
      decimal,
      isSubmitting: false,
      form: {
        name: '',
        description: '',
        active: true,
      },
    }
  },

  watch: {
    priceListEditSidebarActive(newVal) {
      if (newVal) {
        this.loadPriceList()
      }
    },
  },

  methods: {
    async loadPriceList() {
      try {
        const response = await axios.get(`price-list/${this.priceListId}`)
        const priceList = response.data.data

        this.form = {
          name: priceList.name,
          description: priceList.description,
          active: priceList.active,
        }
      } catch (error) {
        this.showErrors(error)
      }
    },

    async onSubmit() {
      try {
        const isValid = await this.$refs.simpleRules.validate()
        if (!isValid) return

        this.isSubmitting = true

        const formData = { ...this.form }

        // Convertir active a boolean si es necesario
        if (typeof formData.active === 'string') {
          formData.active = formData.active === '1' || formData.active === true
        }

        await axios.put(`price-list/${this.priceListId}`, formData)
        this.makeToast('success', 'Lista actualizada', 'La lista de precios se ha actualizado correctamente.')

        this.$emit('refreshData')
        this.$emit('update:price-list-edit-sidebar-active', false)
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.isSubmitting = false
      }
    },

    onReset() {
      this.loadPriceList()
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.data) {
        const errors = error.response.data.data
        Object.keys(errors).forEach(field => {
          this.makeToast('danger', 'Error', `${field}: ${errors[field][0]}`)
        })
      } else {
        this.makeToast('danger', 'Error', 'Ha ocurrido un error inesperado.')
      }
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
